import { CircleUserRound } from 'lucide-react'
import { Link } from 'react-router'

import { link, paths, ownerPaths } from '@/app/router/paths.ts'
import type { Business } from '@/features/business/model/business.ts'
import { monogram, shortName } from '@/lib/format/studioName.ts'

interface AppHeaderProps {
  readonly business: Business
  readonly isOwner?: boolean
  readonly email?: string
}

/**
 * A mark and a short name, sitting on the page rather than in a bar, and on
 * the right — as in the reference — the client profile button. Signed in, it
 * shows the client's initial instead of the empty silhouette.
 */
export function AppHeader({ business, isOwner = false, email = '' }: AppHeaderProps) {

  return (
    <header className="safe-top mx-auto w-full max-w-5xl px-4 sm:px-6">
      <div className="flex h-20 items-center justify-between gap-3 border-b border-border pt-2">
        <Link
          to={link(paths.home)}
          aria-label={`${business.name} — на главную`}
          className="flex min-h-12 items-center gap-3 rounded-xl focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none"
        >
          {/* A mark until studios have logos of their own. */}
          {business.logoUrl ? <img src={business.logoUrl} alt="" width={44} height={44} className="size-11 shrink-0 rounded-full bg-card object-contain" /> : <span
            aria-hidden="true"
            className="grid size-11 shrink-0 place-items-center rounded-full bg-card text-lg font-extrabold ring-1 ring-border"
          >
            {monogram(business.name)}
          </span>}
          <span className="font-heading text-base font-extrabold">{shortName(business.name)}</span>
        </Link>

        <Link
          to={link(isOwner ? ownerPaths.root : paths.account)}
          aria-label={isOwner ? 'Кабинет студии' : email === '' ? 'Профиль клиента — войти' : `Профиль клиента — ${email}`}
          className="grid size-12 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none"
        >
          {email === '' ? (
            <CircleUserRound className="size-7" strokeWidth={1.75} aria-hidden="true" />
          ) : (
            <span
              aria-hidden="true"
              className="grid size-9 place-items-center rounded-full bg-primary text-base font-extrabold text-primary-foreground uppercase"
            >
              {email.slice(0, 1)}
            </span>
          )}
        </Link>
      </div>
    </header>
  )
}
